import { NAV_MODULES } from './app-shell/applicationModuleCatalog.js';
import { isModuleAllowedForRole } from './app-shell/moduleRoleAccess.js';
import familyContentAccess from './shared/familyContentAccessPolicy.js';

// Declaration order is the role priority: accessControl.js builds KNOWN_ROLES from
// Object.values(...) and pickAppRole takes the first match. Keycloak's org_admin is a composite
// that also grants advisor, so org_admin MUST come before advisor or it can never be picked.
// INTRODUCER_MANAGER and SERVICE_PROVIDER_MANAGER are composite of manager (which resolves to
// advisor) so they MUST come before ADVISOR for the same reason.
export const USER_ROLES = Object.freeze({
  ADMIN: 'admin', ORG_ADMIN: 'org_admin',
  INTRODUCER_MANAGER: 'introducer-manager', SERVICE_PROVIDER_MANAGER: 'service-provider-manager',
  ADVISOR: 'advisor',
  INTRODUCER: 'introducer', SERVICE_PROVIDER: 'service-provider',
  PARENT: 'familly_member_parent', KID: 'familly_member_kid',
});

// The Administration module's own tier ladder (unified-administration-module sub-project 1).
// ADMIN, ADVISOR_FIRM, INTRODUCER, and SERVICE_PROVIDER are reachable from their respective
// Keycloak roles. ADVISOR_GROUP and ADVISOR remain reserved for future use and resolve explicitly
// to NONE for every role today, never by falling through: a role this function hasn't been taught
// about must refuse, not default to the first branch that happens to match. administrationSections.js
// reads this value; no other file re-derives an Administration tier from a raw role string.
export const ADMINISTRATION_MODES = Object.freeze({
  ADMIN: 'admin',
  ADVISOR_GROUP: 'advisor-group',
  ADVISOR_FIRM: 'advisor-firm',
  ADVISOR: 'advisor',
  INTRODUCER: 'introducer',
  SERVICE_PROVIDER: 'service-provider',
  NONE: 'none',
});

export function administrationMode(role) {
  const normalized = normalizeUserRole(role);
  if (normalized === USER_ROLES.ADMIN) return ADMINISTRATION_MODES.ADMIN;
  if (normalized === USER_ROLES.ORG_ADMIN) return ADMINISTRATION_MODES.ADVISOR_FIRM;
  if (normalized === USER_ROLES.INTRODUCER_MANAGER) return ADMINISTRATION_MODES.INTRODUCER;
  if (normalized === USER_ROLES.SERVICE_PROVIDER_MANAGER) return ADMINISTRATION_MODES.SERVICE_PROVIDER;
  if (normalized === USER_ROLES.INTRODUCER) return ADMINISTRATION_MODES.INTRODUCER;
  if (normalized === USER_ROLES.SERVICE_PROVIDER) return ADMINISTRATION_MODES.SERVICE_PROVIDER;
  return ADMINISTRATION_MODES.NONE;
}

const ROLE_LABELS = Object.freeze({
  admin: 'Admin', org_admin: 'Org. Admin', advisor: 'Advisor',
  'introducer-manager': 'Introducer Manager', 'service-provider-manager': 'Service Provider Manager',
  introducer: 'Introducer', 'service-provider': 'Service Provider',
  familly_member_parent: 'Family parent', familly_member_kid: 'Family member',
});


export function normalizeUserRole(role) {
  return familyContentAccess.normalizeFamilyContentRole(role);
}
export function getRoleCapabilities(role) {
  const normalized = normalizeUserRole(role);
  const fullWorkspace = normalized === USER_ROLES.ADMIN || normalized === USER_ROLES.ORG_ADMIN || normalized === USER_ROLES.ADVISOR
    || normalized === USER_ROLES.INTRODUCER || normalized === USER_ROLES.SERVICE_PROVIDER
    || normalized === USER_ROLES.INTRODUCER_MANAGER || normalized === USER_ROLES.SERVICE_PROVIDER_MANAGER;
  const canOperateWithoutFamilyScope = normalized === USER_ROLES.ADMIN || normalized === USER_ROLES.ORG_ADMIN;
  const isFamilyMember = normalized === USER_ROLES.PARENT || normalized === USER_ROLES.KID;
  // introducer/service-provider are fullWorkspace (so Training/Repository, gated purely on that
  // flag, stay reachable) but must never be forced through adviserWorkspaceContext.js's mandatory
  // customer/family selection: they have zero assignedFamilyIds by design (canAccessFamily always
  // refuses them), so that selection has nothing to select and hard-blocks the entire app shell
  // before Administration — the one module they're meant to use — ever mounts.
  const requiresCustomerWorkspace = normalized === USER_ROLES.ADMIN || normalized === USER_ROLES.ORG_ADMIN || normalized === USER_ROLES.ADVISOR;
  return {
    fullWorkspace, canOperateWithoutFamilyScope, isFamilyMember, requiresCustomerWorkspace,
    // canSwitchFamily/canSwitchMember/canCustomizeModules/canUseAdvisorTools are keyed off
    // requiresCustomerWorkspace, not bare fullWorkspace (final-review findings, this fix round):
    // introducer/service-provider are fullWorkspace for Training/Repository purposes only — they
    // must not also inherit the family switcher (ApplicationWorkspaceShell.js's
    // WorkspaceScopeControl, whose underlying GET /customers fetch over-shares), the Settings
    // panel's "Show all modules" self-reveal (which bypasses their fixed Administration/Training/
    // Repository nav entirely and can surface 15+ unrelated modules), or advisor-framed Intercom
    // identification. requiresCustomerWorkspace is already the single owner (Task 10) of exactly
    // "admin/org_admin/advisor, not introducer/service-provider" — reusing it here keeps that one
    // fact in one place instead of re-deriving it per capability.
    canSwitchFamily: requiresCustomerWorkspace, canSwitchMember: requiresCustomerWorkspace,
    canViewFamilyProgress: normalized === USER_ROLES.PARENT,
    canCustomizeModules: requiresCustomerWorkspace, canUseAdvisorTools: requiresCustomerWorkspace,
    // The introducer side's two roles that may create/assign linked clients: the contact (INTRODUCER,
    // labelled "Admin" in the Managers tab) and the manager it can promote (INTRODUCER_MANAGER). The
    // contact is the superset — it also gets canManageOwnReferralPartnerManagers below, which a
    // manager does not — not a narrower role excluded from what managers can do. Deliberately no OR
    // with SERVICE_PROVIDER/SERVICE_PROVIDER_MANAGER: service-provider-side roles stay read-only
    // (spec decision #4 — clients originate from introducers, not service providers).
    canManageOwnReferralPartnerClients: normalized === USER_ROLES.INTRODUCER_MANAGER || normalized === USER_ROLES.INTRODUCER,
    // referral-partner-own-managers task 5 fix round: administrationMode() resolves BOTH the
    // contact-login roles (introducer/service-provider) AND the relationship-manager roles
    // (introducer-manager/service-provider-manager) to the SAME ADMINISTRATION_MODES constant, so
    // `mode` alone cannot gate the Managers section to the contact role only (spec decision #1 — only
    // the contact role gets this; the manager roles already manage their own company's managers
    // through the full admin workspace elsewhere). True ONLY for the two contact roles, mirroring
    // exactly how canManageOwnReferralPartnerClients above distinguishes introducer-manager's own
    // extra capability within a mode it shares with a different role.
    canManageOwnReferralPartnerManagers: normalized === USER_ROLES.INTRODUCER || normalized === USER_ROLES.SERVICE_PROVIDER,
  };
}
export function getRoleLabel(role) { return ROLE_LABELS[normalizeUserRole(role)] || 'Unknown role'; }
export function getModulesForRole(role) {
  const normalized = normalizeUserRole(role);
  const access = familyContentAccess.getFamilyContentAccess({ role: normalized, sessionActive: Boolean(normalized) });
  const allowed = NAV_MODULES.filter(module => isModuleAllowedForRole(module, normalized)
    && (module.id !== 'customer-questionnaire-module' || access.canViewQuestionnaire)
    && (module.id !== 'business-profile-module' || access.canViewBusinessProfile));
  if (getRoleCapabilities(normalized).fullWorkspace) return allowed;
  return allowed.filter(module => module.id === 'customer-questionnaire-module' || module.id === 'business-profile-module');
}
export function canRoleAccessModule(role, module) {
  return getModulesForRole(role).some(candidate => candidate.id === (typeof module === 'string' ? module : module?.id));
}
export function getPermissionsForRole(role) {
  if (getRoleCapabilities(role).fullWorkspace) return ['*'];
  return getModulesForRole(role).map(module => module.requiredPermission);
}
export function canAccessFamily(profile, familyId) {
  if (!profile?.sessionActive || !familyId) return false;
  if (profile.role === USER_ROLES.ADMIN) return true;
  if (getRoleCapabilities(profile.role).isFamilyMember) return profile.familyId === familyId;
  // Capability, not role literal. Admin returned above and family members were handled above, so what
  // is left here is exactly the full-workspace non-admin roles — today org_admin, advisor,
  // introducer, and service-provider (introducer/service-provider always have empty
  // assignedFamilyIds by design, so this line is a no-op for them in practice, but the code path is
  // still reached — see the dedicated test for that). Testing `=== USER_ROLES.ADVISOR` here is how
  // adding org_admin silently locked every Org. Admin out of every family: the role gained
  // `fullWorkspace` in the definition above but not the family access that goes with it. Derived from
  // the capability, a sixth full-workspace role inherits family access automatically.
  return getRoleCapabilities(profile.role).fullWorkspace && (profile.assignedFamilyIds || []).includes(familyId);
}
export function filterFamiliesByAccess(families, profile) {
  return (families || []).filter(family => canAccessFamily(profile, family.customerId));
}
